import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from 'sonner';
import { FileText, Upload, CheckCircle, Hourglass, Warning, Paperclip, Trash, X, Receipt, ShieldCheck, ClipboardText, FloppyDisk, PaperPlaneTilt, ChatCircle } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STPortalPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responses, setResponses] = useState({});  // line_id -> {unit_price, note, variant}
  const [totalOverride, setTotalOverride] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('chiffrage');

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/public/st/consultation/${token}`);
      setData(res.data);
      const initial = {};
      (res.data.lines || []).forEach(ln => { initial[ln.id] = ln.response || {}; });
      setResponses(initial);
      setNotes(res.data.response_notes || '');
    } catch (e) {
      toast.error('Lien invalide ou expiré');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const computedTotal = useMemo(() => {
    if (!data) return 0;
    return (data.lines || []).filter(l => (l.kind || 'item') === 'item')
      .reduce((s, l) => s + ((parseFloat(responses[l.id]?.unit_price) || 0) * (l.quantity || 0)), 0);
  }, [data, responses]);

  const updateResponse = (lineId, field, value) => {
    setResponses(prev => ({ ...prev, [lineId]: { ...prev[lineId], [field]: value } }));
  };

  const handleSave = async (submit = false) => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/public/st/consultation/${token}/response`, {
        line_responses: responses,
        notes,
        total_amount_ht: totalOverride ? parseFloat(totalOverride) : computedTotal,
        submit,
      });
      toast.success(submit ? 'Chiffrage soumis ! Merci.' : 'Brouillon enregistré');
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur enregistrement');
    } finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding: 50, textAlign: 'center' }}>Chargement…</div>;
  if (!data) return <div style={{ padding: 50, textAlign: 'center' }}>Lien invalide ou expiré.</div>;

  const isSubmitted = data.status === 'chiffrage_recu' || data.status === 'valide' || data.status === 'refuse';

  return (
    <div style={{ minHeight: '100vh', background: '#F4F5F7', paddingBottom: 40 }} data-testid="st-portal-page">
      <Toaster position="top-right" richColors />
      <header style={{ background: '#002FA7', color: 'white', padding: '18px 28px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.4rem', letterSpacing: '-0.5px' }}>BATI-BOYS · Portail Sous-Traitant</h1>
            <p style={{ margin: '4px 0 0', opacity: 0.85, fontSize: '0.9rem' }}>{data.subcontractor_name} · {data.project_name}{data.corps_etat ? ` · ${data.corps_etat}` : ''}</p>
          </div>
          <StatusPill status={data.status} />
        </div>
      </header>

      <main style={{ maxWidth: 1100, margin: '20px auto', padding: '0 24px' }}>
        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #E2E2E6', marginBottom: 22, flexWrap: 'wrap' }}>
          {[
            { k: 'chiffrage', l: 'Chiffrage', I: ClipboardText },
            { k: 'docs', l: 'Documents obligatoires', I: ShieldCheck },
            { k: 'facturation', l: 'Facturation', I: Receipt },
            { k: 'reserves', l: 'Réserves', I: Warning },
            { k: 'correspondance', l: 'Correspondance', I: ChatCircle },
          ].map(({ k, l, I }) => (
            <button key={k} onClick={() => setActiveTab(k)} data-testid={`st-tab-${k}`}
              style={{ padding: '12px 22px', border: 'none', background: 'transparent', cursor: 'pointer',
                fontWeight: activeTab === k ? 700 : 500, color: activeTab === k ? '#002FA7' : '#5E5F66',
                borderBottom: activeTab === k ? '3px solid #002FA7' : '3px solid transparent', marginBottom: -2,
                display: 'inline-flex', alignItems: 'center', gap: 8 }}><I size={16} /> {l}</button>
          ))}
        </div>

        <OtherProjectsBar token={token} />

        {activeTab === 'chiffrage' && (
          <ChiffrageTab
            data={data} responses={responses} updateResponse={updateResponse}
            computedTotal={computedTotal} totalOverride={totalOverride} setTotalOverride={setTotalOverride}
            notes={notes} setNotes={setNotes}
            onSave={() => handleSave(false)} onSubmit={() => handleSave(true)}
            saving={saving} isSubmitted={isSubmitted} token={token} onRefresh={fetchData}
          />
        )}
        {activeTab === 'docs' && <DocsTab data={data} token={token} onRefresh={fetchData} />}
        {activeTab === 'facturation' && <FacturationTab data={data} token={token} />}
        {activeTab === 'reserves' && <ReservesTab data={data} token={token} />}
        {activeTab === 'correspondance' && <CorrespondenceTab data={data} token={token} />}
      </main>
    </div>
  );
};

// ============================================================================
// Bar showing other consultations of the same ST (multi-chantier navigation)
// ============================================================================
const OtherProjectsBar = ({ token }) => {
  const [others, setOthers] = useState([]);
  useEffect(() => {
    axios.get(`${API_URL}/api/public/st/consultation/${token}/other-projects`)
      .then(r => setOthers(r.data || []))
      .catch(() => {});
  }, [token]);
  if (others.length === 0) return null;
  return (
    <div className="card" style={{ padding: '10px 14px', marginBottom: 18, background: '#EFF6FF', borderLeft: '4px solid #002FA7' }} data-testid="st-other-projects-bar">
      <div style={{ fontSize: '0.78rem', color: '#5E5F66', marginBottom: 6, fontWeight: 600 }}>📋 Vos autres consultations ({others.length})</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {others.map(o => (
          <a key={o.token} href={`/st/offer/${o.token}`} data-testid={`other-project-${o.token}`}
             style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: 'white', borderRadius: 6, border: '1px solid #E2E2E6', color: '#002FA7', fontWeight: 600, fontSize: '0.85rem', textDecoration: 'none' }}>
            <span>{o.project_name}</span>
            {o.corps_etat && <span style={{ fontSize: '0.7rem', color: '#5E5F66' }}>· {o.corps_etat}</span>}
            <span style={{ fontSize: '0.65rem', padding: '1px 5px', borderRadius: 3, background: o.status === 'valide' ? '#E8F5E9' : o.status === 'chiffrage_recu' ? '#E3F2FD' : '#FEF3C7', color: o.status === 'valide' ? '#2E7D32' : o.status === 'chiffrage_recu' ? '#1565C0' : '#92400E' }}>{o.status}</span>
          </a>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Tab : Correspondance
// ============================================================================
const CorrespondenceTab = ({ data, token }) => {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const fetch = useCallback(async () => {
    const r = await axios.get(`${API_URL}/api/public/st/consultation/${token}/messages`);
    setMessages(r.data || []);
  }, [token]);
  useEffect(() => { fetch(); }, [fetch]);
  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/public/st/consultation/${token}/messages`, { body });
      setBody('');
      fetch();
    } catch { toast.error('Erreur envoi'); }
    finally { setSending(false); }
  };
  return (
    <div data-testid="st-correspondence-tab">
      <div className="card" style={{ padding: 0, maxHeight: 460, overflowY: 'auto', marginBottom: 14 }}>
        {messages.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#5E5F66', padding: 30 }}>Aucun message échangé pour le moment.</p>
        ) : messages.map(m => (
          <div key={m.id} style={{ padding: '10px 14px', borderBottom: '1px solid #F4F5F7', background: m.author === 'admin' ? '#F4F5F7' : 'white' }} data-testid={`st-msg-${m.id}`}>
            <div style={{ fontSize: '0.72rem', color: '#5E5F66', marginBottom: 4, fontWeight: 600 }}>
              {m.author === 'admin' ? '🏢 Donneur d\'ordre' : '👷 Vous'} · {new Date(m.created_at).toLocaleString('fr-FR')}
            </div>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.92rem' }}>{m.body}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
        <textarea className="form-input" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Écrire un message…" rows={3} style={{ flex: 1 }} data-testid="st-msg-input" />
        <button className="btn btn-primary" onClick={send} disabled={sending || !body.trim()} data-testid="st-msg-send"><PaperPlaneTilt size={14} /> Envoyer</button>
      </div>
    </div>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    envoye: { l: 'Chiffrage en attente', bg: '#FEF3C7', c: '#92400E', I: Hourglass },
    chiffrage_recu: { l: 'Chiffrage soumis', bg: '#E3F2FD', c: '#1565C0', I: CheckCircle },
    valide: { l: 'Validé par le donneur d\'ordre', bg: '#E8F5E9', c: '#2E7D32', I: CheckCircle },
    refuse: { l: 'Refusé', bg: '#FFEBEE', c: '#C62828', I: Warning },
  };
  const cfg = map[status] || map.envoye;
  return <span style={{ background: cfg.bg, color: cfg.c, padding: '6px 14px', borderRadius: 999, fontWeight: 700, fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}><cfg.I size={14} /> {cfg.l}</span>;
};

// ============================================================================
// Tab : Chiffrage
// ============================================================================
const ChiffrageTab = ({ data, responses, updateResponse, computedTotal, totalOverride, setTotalOverride, notes, setNotes, onSave, onSubmit, saving, isSubmitted, token, onRefresh }) => {
  const techInputRef = useRef(null);
  const uploadTech = async (file) => {
    const form = new FormData();
    form.append('file', file);
    try {
      await axios.post(`${API_URL}/api/public/st/consultation/${token}/tech-attachment`, form);
      toast.success('Fichier ajouté');
      onRefresh();
    } catch { toast.error('Erreur upload'); }
  };
  const removeTech = async (aid) => {
    await axios.delete(`${API_URL}/api/public/st/consultation/${token}/tech-attachment/${aid}`);
    toast.success('Supprimé');
    onRefresh();
  };

  return (
    <div>
      {data.message && (
        <div className="card" style={{ background: '#FFFBEB', borderLeft: '4px solid #F59E0B', padding: '14px 18px', marginBottom: 20 }}>
          <strong>Message du donneur d'ordre :</strong>
          <p style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap' }}>{data.message}</p>
        </div>
      )}

      {data.admin_attachments?.length > 0 && (
        <div className="card" style={{ padding: '14px 18px', marginBottom: 20 }}>
          <h4 style={{ margin: '0 0 10px', fontSize: '0.95rem' }}>📎 Documents fournis par le donneur d'ordre</h4>
          {data.admin_attachments.map(d => (
            <div key={d.doc_id || d.filename} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 4 }}>
              <FileText size={14} /> <span>{d.original_filename || d.filename}</span>
            </div>
          ))}
        </div>
      )}

      <div className="card" style={{ padding: 0, marginBottom: 20, overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Saisie de votre offre par ligne</h3>
          <span style={{ fontSize: '0.85rem', color: '#5E5F66' }}>Total calculé : <strong style={{ color: '#002FA7' }}>{computedTotal.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € HT</strong></span>
        </div>
        <table className="data-table" data-testid="st-chiffrage-table" style={{ margin: 0 }}>
          <thead><tr>
            <th style={{ width: 38 }}>N°</th><th>Désignation</th>
            <th style={{ width: 70, textAlign: 'center' }}>Unité</th>
            <th style={{ width: 70, textAlign: 'right' }}>Qté</th>
            <th style={{ width: 130, textAlign: 'right' }}>PU HT</th>
            <th style={{ width: 110, textAlign: 'right' }}>Total HT</th>
            <th style={{ width: 240 }}>Note / Variante</th>
          </tr></thead>
          <tbody>
            {(() => {
              let itemNum = 0;
              return (data.lines || []).map(ln => {
                const kind = ln.kind || 'item';
                if (kind === 'header') {
                  return <tr key={ln.id} style={{ background: '#002FA7', color: 'white' }}>
                    <td colSpan={7} style={{ padding: '8px 14px', fontWeight: 700 }}>{ln.description}</td>
                  </tr>;
                }
                if (kind === 'note') {
                  return <tr key={ln.id} style={{ background: '#FFFBEB', color: '#92400E' }}>
                    <td colSpan={7} style={{ padding: '8px 14px', fontStyle: 'italic' }}>📝 {ln.description}</td>
                  </tr>;
                }
                itemNum++;
                const r = responses[ln.id] || {};
                const up = parseFloat(r.unit_price) || 0;
                const tot = up * (ln.quantity || 0);
                return (
                  <tr key={ln.id} data-testid={`st-line-${ln.id}`}>
                    <td style={{ textAlign: 'center', color: '#5E5F66' }}>{itemNum}</td>
                    <td><strong>{ln.description}</strong>{ln.details && <div style={{ fontSize: '0.78rem', color: '#5E5F66' }}>{ln.details}</div>}</td>
                    <td style={{ textAlign: 'center' }}>{ln.unit}</td>
                    <td style={{ textAlign: 'right' }}>{ln.quantity}</td>
                    <td style={{ textAlign: 'right' }}>
                      <input type="number" step="0.01" disabled={isSubmitted}
                        className="form-input" style={{ width: 110, textAlign: 'right', height: 30, fontSize: '0.9rem' }}
                        value={r.unit_price || ''}
                        onChange={(e) => updateResponse(ln.id, 'unit_price', e.target.value)}
                        data-testid={`st-line-pu-${ln.id}`} placeholder="0.00" />
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: '#002FA7' }}>{tot.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</td>
                    <td>
                      <input type="text" disabled={isSubmitted} className="form-input" style={{ height: 28, fontSize: '0.8rem' }}
                        value={r.note || ''} placeholder="Note (opt.)"
                        onChange={(e) => updateResponse(ln.id, 'note', e.target.value)}
                        data-testid={`st-line-note-${ln.id}`} />
                      <input type="text" disabled={isSubmitted} className="form-input" style={{ height: 28, fontSize: '0.8rem', marginTop: 4 }}
                        value={r.variant || ''} placeholder="Variante (opt.)"
                        onChange={(e) => updateResponse(ln.id, 'variant', e.target.value)} />
                    </td>
                  </tr>
                );
              });
            })()}
          </tbody>
        </table>
      </div>

      <div className="card" style={{ padding: '14px 18px', marginBottom: 20 }}>
        <h4 style={{ margin: '0 0 10px', fontSize: '0.95rem' }}>📁 Pièces jointes (plans, fiches techniques, variantes…)</h4>
        <input ref={techInputRef} type="file" hidden onChange={(e) => e.target.files?.[0] && uploadTech(e.target.files[0])} data-testid="st-tech-input" />
        <button className="btn btn-secondary" disabled={isSubmitted} onClick={() => techInputRef.current?.click()} data-testid="st-tech-upload-btn">
          <Upload size={14} /> Ajouter un fichier
        </button>
        <div style={{ marginTop: 10 }}>
          {(data.tech_attachments || []).map(a => (
            <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid #F4F5F7' }}>
              <Paperclip size={14} color="#5E5F66" />
              <a href={`${API_URL}${a.url}`} target="_blank" rel="noreferrer" style={{ flex: 1, color: '#002FA7' }}>{a.original_filename}</a>
              {!isSubmitted && <button className="btn btn-danger btn-sm" onClick={() => removeTech(a.id)} data-testid={`st-tech-del-${a.id}`}><Trash size={12} /></button>}
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ padding: '14px 18px', marginBottom: 20 }}>
        <h4 style={{ margin: '0 0 10px', fontSize: '0.95rem' }}>Notes générales / Conditions</h4>
        <textarea disabled={isSubmitted} className="form-input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Délais, conditions, exclusions…" data-testid="st-notes" />
        <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: '0.9rem' }}>Total HT proposé (si différent du calculé) :</label>
          <input type="number" step="0.01" disabled={isSubmitted} className="form-input" style={{ width: 160, textAlign: 'right' }} value={totalOverride} onChange={(e) => setTotalOverride(e.target.value)} placeholder={computedTotal.toFixed(2)} data-testid="st-total-override" />
        </div>
      </div>

      {!isSubmitted ? (
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={onSave} disabled={saving} data-testid="st-save-draft-btn"><FloppyDisk size={14} /> Sauvegarder le brouillon</button>
          <button className="btn btn-primary" onClick={onSubmit} disabled={saving} data-testid="st-submit-btn" style={{ background: '#002FA7' }}><PaperPlaneTilt size={14} /> Soumettre mon offre</button>
        </div>
      ) : (
        <div className="card" style={{ background: '#E8F5E9', borderLeft: '4px solid #2E7D32', padding: '14px 18px', color: '#1B5E20', display: 'flex', alignItems: 'center', gap: 10 }}>
          <CheckCircle size={20} /> Votre offre a bien été soumise. Le donneur d'ordre vous recontactera prochainement.
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Tab : Documents obligatoires
// ============================================================================
const DocsTab = ({ data, token, onRefresh }) => {
  const [uploading, setUploading] = useState({});
  const upload = async (docKey, file, expiry) => {
    setUploading(p => ({ ...p, [docKey]: true }));
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('doc_type', docKey);
      if (expiry) form.append('expiry_date', expiry);
      await axios.post(`${API_URL}/api/public/st/consultation/${token}/required-doc`, form);
      toast.success('Document enregistré');
      onRefresh();
    } catch { toast.error('Erreur upload'); }
    finally { setUploading(p => ({ ...p, [docKey]: false })); }
  };

  return (
    <div>
      <div className="card" style={{ padding: '14px 18px', marginBottom: 20, background: '#EFF6FF', borderLeft: '4px solid #002FA7' }}>
        <p style={{ margin: 0 }}>Pour valider votre intervention, veuillez fournir les 6 documents obligatoires ci-dessous (validité à jour).</p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
        {data.required_doc_types.map(dt => {
          const existing = data.submitted_required_docs.find(d => d.doc_type === dt.key);
          return <DocCard key={dt.key} docType={dt} existing={existing} onUpload={(file, expiry) => upload(dt.key, file, expiry)} uploading={uploading[dt.key]} />;
        })}
      </div>
    </div>
  );
};

const DocCard = ({ docType, existing, onUpload, uploading }) => {
  const inputRef = useRef(null);
  const [expiry, setExpiry] = useState(existing?.expiry_date || '');
  const isExpired = useMemo(() => {
    if (!existing?.expiry_date) return false;
    return new Date(existing.expiry_date) < new Date();
  }, [existing]);
  return (
    <div className="card" style={{ padding: '14px 16px', border: existing ? (isExpired ? '2px solid #C62828' : '2px solid #2E7D32') : '2px dashed #E2E2E6' }} data-testid={`doc-card-${docType.key}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div>
          <strong>{docType.label}</strong>
          {existing && <div style={{ fontSize: '0.78rem', color: '#5E5F66', marginTop: 4 }}>{existing.original_filename}</div>}
        </div>
        {existing && <span style={{ background: isExpired ? '#FFEBEE' : '#E8F5E9', color: isExpired ? '#C62828' : '#2E7D32', padding: '2px 8px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 600 }}>{isExpired ? 'EXPIRÉ' : 'OK'}</span>}
      </div>
      <div style={{ marginTop: 10 }}>
        <label style={{ fontSize: '0.78rem', color: '#5E5F66' }}>Date de validité (si applicable)</label>
        <input type="date" className="form-input" style={{ height: 32 }} value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid={`doc-expiry-${docType.key}`} />
      </div>
      <input ref={inputRef} type="file" hidden onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0], expiry)} data-testid={`doc-input-${docType.key}`} />
      <button className="btn btn-secondary btn-sm" style={{ marginTop: 8, width: '100%' }} disabled={uploading} onClick={() => inputRef.current?.click()} data-testid={`doc-upload-${docType.key}`}>
        <Upload size={12} /> {existing ? 'Remplacer' : 'Téléverser'}
      </button>
    </div>
  );
};

// ============================================================================
// Tab : Facturation
// ============================================================================
const FacturationTab = ({ data, token }) => {
  const [invoices, setInvoices] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const fetchInv = useCallback(async () => {
    const r = await axios.get(`${API_URL}/api/public/st/consultation/${token}/invoices`);
    setInvoices(r.data || []);
  }, [token]);
  useEffect(() => { fetchInv(); }, [fetchInv]);
  return (
    <div>
      {/* TVA 0% Autoliquidation warning */}
      <div data-testid="tva-autoliq-warning" style={{ background: '#FEE2E2', border: '2px solid #C62828', borderRadius: 8, padding: '14px 18px', marginBottom: 18, display: 'flex', gap: 12, alignItems: 'center' }}>
        <Warning size={28} color="#C62828" weight="fill" />
        <div>
          <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#C62828', textTransform: 'uppercase' }}>TVA 0 % - AUTOLIQUIDATION</div>
          <div style={{ fontSize: '0.9rem', color: '#7F1D1D', marginTop: 4, fontWeight: 600 }}>
            En tant que sous-traitant BTP, votre facture <u>doit obligatoirement</u> mentionner :
            « <strong>Autoliquidation – TVA due par le preneur (art. 283-2 nonies du CGI)</strong> »
            <br/>La TVA <strong>doit être à 0 %</strong>. Toute facture sans cette mention sera refusée.
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Mes factures</h3>
        <button className="btn btn-primary" onClick={() => setShowModal(true)} data-testid="st-new-invoice-btn"><Receipt size={14} /> Nouvelle facture</button>
      </div>
      {invoices.length === 0 ? (
        <div className="card" style={{ padding: 30, textAlign: 'center', color: '#5E5F66' }}>Aucune facture pour le moment.</div>
      ) : (
        <table className="data-table" data-testid="st-invoices-table">
          <thead><tr><th>N°</th><th>Date</th><th>Description</th><th style={{ textAlign: 'right' }}>Montant HT</th><th>Vérif. TVA</th><th>Statut</th><th></th></tr></thead>
          <tbody>
            {invoices.map(i => {
              const cfg = { en_attente: { l: 'En attente', c: '#1565C0', bg: '#E3F2FD' }, validee: { l: 'Validée', c: '#2E7D32', bg: '#E8F5E9' }, refusee: { l: 'Refusée', c: '#C62828', bg: '#FFEBEE' }, payee: { l: 'Payée', c: '#047857', bg: '#D1FAE5' } }[i.validation_status] || { l: i.validation_status, c: '#5E5F66', bg: '#F4F5F7' };
              const tvc = i.tva_check;
              const tvaOk = tvc && tvc.tva_ok && tvc.mention_ok;
              return (
                <tr key={i.id} data-testid={`st-invoice-${i.id}`} style={{ background: i.validation_status === 'refusee' ? '#FEF2F2' : 'white' }}>
                  <td><strong>{i.number}</strong></td>
                  <td>{i.date}</td>
                  <td>{i.description}{i.validation_status === 'refusee' && i.rejection_reason && <div style={{ fontSize: '0.78rem', color: '#C62828', marginTop: 2 }}>Motif : {i.rejection_reason}</div>}</td>
                  <td style={{ textAlign: 'right' }}>{i.amount_ht.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</td>
                  <td>
                    {tvc ? (
                      <span title={tvc.comment || ''} style={{ padding: '3px 10px', borderRadius: 4, fontWeight: 600, fontSize: '0.72rem', background: tvaOk ? '#E8F5E9' : '#FFEBEE', color: tvaOk ? '#2E7D32' : '#C62828' }}>
                        {tvaOk ? '✓ Conforme' : `✗ Non conforme${tvc.tva_rate_found != null ? ` (TVA ${tvc.tva_rate_found}%)` : ''}`}
                      </span>
                    ) : <span style={{ fontSize: '0.72rem', color: '#9CA3AF' }}>Non vérifiée</span>}
                  </td>
                  <td><span style={{ padding: '3px 10px', borderRadius: 4, fontWeight: 600, fontSize: '0.72rem', background: cfg.bg, color: cfg.c }}>{cfg.l}</span></td>
                  <td>
                    {i.pdf_url && (
                      <button
                        className="btn btn-secondary btn-sm"
                        title="Vérifier TVA / Autoliquidation avec IA"
                        data-testid={`st-check-tva-${i.id}`}
                        onClick={async () => {
                          toast.message('Analyse IA en cours…');
                          try {
                            const r = await axios.post(`${API_URL}/api/public/st/consultation/${token}/invoice/${i.id}/check-tva`);
                            const ck = r.data?.check;
                            toast.success(ck?.tva_ok && ck?.mention_ok ? 'Facture conforme' : 'Non conforme — vérifiez votre facture');
                            fetchInv();
                          } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur Gemini'); }
                        }}
                      >🤖 Vérifier</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {showModal && <NewInvoiceModal data={data} token={token} onClose={() => setShowModal(false)} onCreated={() => { setShowModal(false); fetchInv(); }} />}
    </div>
  );
};

const NewInvoiceModal = ({ data, token, onClose, onCreated }) => {
  const items = (data.lines || []).filter(l => (l.kind || 'item') === 'item');
  const [form, setForm] = useState({ number: '', date: new Date().toISOString().slice(0, 10), description: '', amount_ht: '', tva_rate: 20 });
  const [advances, setAdvances] = useState({}); // line_id -> pct
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const total = useMemo(() => items.reduce((s, l) => {
    const pct = parseFloat(advances[l.id]) || 0;
    const pu = parseFloat(l.response?.unit_price) || 0;
    return s + (pu * (l.quantity || 0) * pct / 100);
  }, 0), [items, advances]);
  useEffect(() => { setForm(f => ({ ...f, amount_ht: total.toFixed(2) })); }, [total]);
  const submit = async () => {
    if (!form.number.trim()) return toast.error('Numéro requis');
    if (parseFloat(form.amount_ht) <= 0) return toast.error('Montant HT requis');
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('number', form.number);
      fd.append('date', form.date);
      fd.append('description', form.description);
      fd.append('amount_ht', form.amount_ht);
      fd.append('tva_rate', form.tva_rate);
      const adv = items.map(l => ({ line_id: l.id, description: l.description, advancement_pct: parseFloat(advances[l.id]) || 0 }));
      fd.append('advancement_lines', JSON.stringify(adv));
      if (file) fd.append('file', file);
      await axios.post(`${API_URL}/api/public/st/consultation/${token}/invoice`, fd);
      toast.success('Facture soumise');
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSubmitting(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 880, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>Nouvelle facture</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 14 }}>
            <input className="form-input" placeholder="N° de facture *" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} data-testid="inv-form-number" />
            <input type="date" className="form-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            <input type="number" className="form-input" placeholder="TVA %" value={form.tva_rate} onChange={(e) => setForm({ ...form, tva_rate: e.target.value })} />
          </div>
          <input className="form-input" placeholder="Description" style={{ marginBottom: 14 }} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />

          <h4 style={{ margin: '0 0 8px' }}>Avancement par poste</h4>
          <table className="data-table">
            <thead><tr><th>Désignation</th><th style={{ textAlign: 'right' }}>PU</th><th style={{ textAlign: 'right' }}>Qté</th><th style={{ width: 110, textAlign: 'right' }}>% Avancement</th><th style={{ textAlign: 'right' }}>HT</th></tr></thead>
            <tbody>
              {items.map(l => {
                const pct = parseFloat(advances[l.id]) || 0;
                const pu = parseFloat(l.response?.unit_price) || 0;
                const v = pu * (l.quantity || 0) * pct / 100;
                return <tr key={l.id}>
                  <td>{l.description}</td>
                  <td style={{ textAlign: 'right' }}>{pu.toFixed(2)} €</td>
                  <td style={{ textAlign: 'right' }}>{l.quantity} {l.unit}</td>
                  <td><input type="number" min="0" max="100" className="form-input" style={{ width: 80, textAlign: 'right', height: 28 }} value={advances[l.id] || ''} onChange={(e) => setAdvances(p => ({ ...p, [l.id]: e.target.value }))} data-testid={`inv-adv-${l.id}`} /></td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{v.toFixed(2)} €</td>
                </tr>;
              })}
            </tbody>
            <tfoot><tr><td colSpan={4} style={{ textAlign: 'right', fontWeight: 700 }}>TOTAL HT</td><td style={{ textAlign: 'right', fontWeight: 700, color: '#002FA7' }}>{total.toFixed(2)} €</td></tr></tfoot>
          </table>

          <div style={{ marginTop: 14 }}>
            <label className="form-label">PDF de la facture (optionnel)</label>
            <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0])} data-testid="inv-file-input" />
          </div>
        </div>
        <div style={{ padding: '14px 20px', borderTop: '1px solid #E2E2E6', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={submit} disabled={submitting} data-testid="inv-submit-btn">{submitting ? 'Envoi…' : 'Soumettre'}</button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Tab : Réserves
// ============================================================================
const ReservesTab = ({ data, token }) => {
  const [reserves, setReserves] = useState([]);
  const fetchR = useCallback(async () => {
    const r = await axios.get(`${API_URL}/api/public/st/consultation/${token}/reservations`);
    setReserves(r.data || []);
  }, [token]);
  useEffect(() => { fetchR(); }, [fetchR]);

  const uploadQuitus = async (rid, file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API_URL}/api/public/st/consultation/${token}/reservation/${rid}/quitus`, fd);
      toast.success('Quitus envoyé');
      fetchR();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  if (reserves.length === 0) return <div className="card" style={{ padding: 30, textAlign: 'center', color: '#5E5F66' }}>Aucune réserve assignée pour le moment.</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {reserves.map(r => <ReserveCard key={r.id} reserve={r} onUploadQuitus={(f) => uploadQuitus(r.id, f)} />)}
    </div>
  );
};

const ReserveCard = ({ reserve, onUploadQuitus }) => {
  const inputRef = useRef(null);
  const isLifted = reserve.status === 'leve' || reserve.status === 'leve_par_st';
  return (
    <div className="card" style={{ padding: '14px 18px' }} data-testid={`reserve-${reserve.id}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <strong>{reserve.title}</strong>
          <p style={{ margin: '4px 0', fontSize: '0.85rem', color: '#5E5F66' }}>{reserve.description}</p>
          {reserve.location && <p style={{ fontSize: '0.78rem', color: '#9CA3AF', margin: 0 }}>📍 {reserve.location}</p>}
        </div>
        <span style={{ background: isLifted ? '#E8F5E9' : '#FEF3C7', color: isLifted ? '#2E7D32' : '#92400E', padding: '4px 12px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600 }}>{isLifted ? 'Levée' : 'À lever'}</span>
      </div>
      {(reserve.quitus_files || []).length > 0 && (
        <div style={{ marginTop: 8 }}>
          {reserve.quitus_files.map(q => (
            <a key={q.id} href={`${API_URL}${q.url}`} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', background: '#EFF6FF', borderRadius: 4, color: '#002FA7', fontSize: '0.8rem', marginRight: 6 }}><Paperclip size={12} /> {q.original_filename}</a>
          ))}
        </div>
      )}
      {!isLifted && (
        <div style={{ marginTop: 10 }}>
          <input ref={inputRef} type="file" hidden onChange={(e) => e.target.files?.[0] && onUploadQuitus(e.target.files[0])} data-testid={`reserve-quitus-input-${reserve.id}`} />
          <button className="btn btn-primary btn-sm" onClick={() => inputRef.current?.click()} data-testid={`reserve-quitus-btn-${reserve.id}`}><Upload size={12} /> Téléverser le quitus (PDF / photo)</button>
        </div>
      )}
    </div>
  );
};

export default STPortalPage;
